import { BiLock } from "react-icons/bi";
import { RcFile } from "antd/lib/upload";
import { useEffect, useState } from "react";
import Dragger from "antd/lib/upload/Dragger";
import { useNavigate } from "react-router-dom";
import { MdOutlineLogout } from "react-icons/md";
import { DownloadOutlined } from "@ant-design/icons";
import {
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Typography,
  UploadFile,
  UploadProps,
} from "antd";
import api from "../../services/api";
import Button from "../../components/Button";
import { Faturamento } from "../Faturamento";
import { Container, Content } from "./styles";
import { useAuth } from "../../contexts/AuthContext";
import {
  validationEmail,
  validationFieldRequired,
  validationFullName,
  validationLogo,
  validationPassword,
} from "../../utils/formValidations";

export const MinhaConta: React.FC = () => {
  const { user, setUser, signOut, updateUserInfo } = useAuth();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordAlreadyExists, setPasswordAlreadyExists] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(user.user.fileLink);
  const [previewTitle, setPreviewTitle] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>(
    user.user.fileLink
      ? [
          {
            uid: "-1",
            name: "Logotipo",
            status: "done",
            url: `${user.user.fileLink}`,
          },
        ]
      : []
  );

  useEffect(() => {
    if (user?.user) {
      setPasswordAlreadyExists(user.user.hasPassword);
      form.setFieldsValue({
        username: user.user.username,
        name: user.user.name,
        email: user.user.email,
      });
    }
  }, [user.user, form]);

  useEffect(() => {
    if (!fileList || fileList.length === 0) setPreviewImage("");
  }, [fileList]);

  const onFinish = (data: any) => {
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("name", data.name);
    if (fileList && fileList.length > 0 && data?.logo?.file?.originFileObj)
      formData.append("files", data?.logo?.file?.originFileObj as File);
    if (showPassword) {
      if (passwordAlreadyExists) {
        formData.append("password", data.pass);
        formData.append("currentPassword", data.currentPass);
      } else {
        formData.append("password", data.pass);
      }
    }

    setLoading(true);
    api
      .post(`/accountants/logo/${data.username}`, formData)
      .then(() => {
        message.config({ top: 90 });
        message.success("Dados atualizados com sucesso");
        setLoading(false);
        updateUserInfo();
        setUser({
          ...user,
          user: {
            ...user.user,
            email: data.email,
            name: data.name,
            ...(showPassword
              ? !passwordAlreadyExists
                ? {
                    hasPassword: true,
                  }
                : {}
              : {}),
          },
        });
      })
      .catch((err) => {
        message.error(
          err.response?.data?.message ||
            "Erro ao atualizar dados. Tente novamente mais tarde"
        );
        setLoading(false);
      });
  };

  const onCancel = () => {
    navigate("/clients");
  };

  const handlePassword = () => {
    setShowPassword((show) => !show);
  };

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  const handleChange: UploadProps["onChange"] = async ({
    fileList: newFileList,
  }) => {
    const conditions = ["image/jpeg", "image/png"];
    const file = newFileList[0];
    setFileList(newFileList);
    const preview = await getBase64(file.originFileObj as RcFile);
    setPreviewImage(preview);
    message.config({ top: 40 });
    const { status } = file;
    if (status === "done") {
      message.success(`Logotipo ${file.name} carregado com sucesso.`);
    } else if (status === "error") {
      if (
        file?.size &&
        file.size > 15000 &&
        conditions.some((el) => file.type?.includes(el))
      )
        message.error(`Tamanho máximo permitido: 15KB.`);
      else if (file.type && !conditions.some((el) => file.type?.includes(el)))
        message.error(`Formato de arquivo inválido.`);
      else
        message.error(`Houve um problema ao carregar o logotipo ${file.name}.`);
    }
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    action: "https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188",
    onDrop(e) {
      console.log("Arquivo carregado", e.dataTransfer.files);
    },
  };

  return (
    <Container>
      {/* {(user.user.office || user?.user?.admin) && <Faturamento />} */}
      <Content>
        <Typography.Title level={1} className="app-title">
          Dados do usuário
        </Typography.Title>
        <br />
        <Form form={form} size="large" layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Nome completo"
            rules={validationFullName}
          >
            <Input placeholder="Nome completo" />
          </Form.Item>

          <Form.Item
            name="username"
            label="ID do usuário"
            rules={validationFieldRequired}
          >
            <Input disabled={true} placeholder="ID do usuário" />
          </Form.Item>

          <Form.Item name="email" label="E-mail" rules={validationEmail}>
            <Input placeholder="e-mail" disabled={loading} />
          </Form.Item>

          {(user.user.office || user?.user?.admin) && (
            <Form.Item
              name="logo"
              rules={validationLogo}
              label="Logotipo do escritório"
              style={{ fontWeight: "bold" }}
            >
              <Dragger
                {...props}
                maxCount={1}
                fileList={fileList}
                onChange={handleChange}
                onPreview={handlePreview}
                accept="image/png, image/jpeg"
              >
                <div className="upload-logo">
                  <p
                    className="upload-drag-icon"
                    style={{
                      backgroundColor: `${previewImage ? "#E8E8E8" : ""}`,
                    }}
                  >
                    {previewImage ? (
                      <img alt="Logo" src={previewImage} />
                    ) : (
                      <DownloadOutlined width="auto" height="auto" />
                    )}
                  </p>
                  <div className="upload-text">
                    <p className="ant-upload-text">
                      Clique ou arraste seu logotipo
                    </p>
                    <p className="ant-upload-hint">
                      Obs: Recomendamos arquivos .PNG com fundo transparente e
                      tamanho de no máximo 125x50px até 15KB
                    </p>
                  </div>
                </div>
              </Dragger>
            </Form.Item>
          )}

          <Modal
            open={previewOpen}
            title={previewTitle}
            footer={null}
            onCancel={() => setPreviewOpen(false)}
          >
            <img alt="Logo" style={{ width: "100%" }} src={previewImage} />
          </Modal>

          <Button
            type="link"
            onClick={handlePassword}
            icon={<BiLock size={18} />}
          >
            {showPassword ? "Não alterar senha" : "Alterar senha"}
          </Button>

          {showPassword && (
            <Row gutter={[16, 16]}>
              {passwordAlreadyExists && (
                <Col span={12}>
                  <Form.Item
                    name="currentPass"
                    label="Senha atual"
                    rules={validationFieldRequired}
                  >
                    <Input.Password
                      visibilityToggle={false}
                      placeholder="*****"
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={12}>
                <Form.Item
                  name="pass"
                  label="Nova senha"
                  rules={validationPassword}
                >
                  <Input.Password
                    visibilityToggle={false}
                    placeholder="*****"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <br />

          <Row gutter={[16, 16]} justify="space-between">
            <Col span={12}>
              <Button block size="large" onClick={onCancel} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            <Col span={12}>
              <Button
                block
                size="large"
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Salvar
              </Button>
            </Col>
          </Row>
        </Form>
      </Content>
      <Content className="transparent">
        <Button
          type="link"
          size="large"
          onClick={signOut}
          className="signout"
          icon={<MdOutlineLogout />}
        >
          Sair
        </Button>
      </Content>
    </Container>
  );
};
